import { Button } from "primereact/button";
import { useEffect } from "react";
import styled from "styled-components";
import { MODULES } from "training/curriculum/modules";
import { TourModule } from "training/types";

/**
 * CLE-3015 (John's review: "a clean divide between modules … some type of title slide … white out the
 * background so it feels like it's transitioning to a new module"). Shown by the engine before the first step
 * of each module: a centred card over an OPAQUE backdrop, so the page behind is blanked, with the module's
 * number, title, purpose and, when the curriculum sets it, how long it takes. "Start Module N" starts the
 * module's steps; Exit saves the place, like the popover's Exit.
 *
 * Generated from MODULES, never a curriculum step: it has no anchor, no step id and no place in the saved
 * progress. Above everything the app renders (the driver.js popover's z-index, which nothing sits over).
 */
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000000000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: var(--brand-blue-25, #f5f7ff);
  font-family: var(--font-family);
  color: var(--brand-black);
`;

const Card = styled.div`
  width: 100%;
  max-width: 520px;
  padding: 32px;
  border: 1px solid var(--brand-gray-200);
  border-radius: 16px;
  background: var(--brand-white, #fff);
  box-shadow: 0 12px 40px rgba(16, 24, 40, 0.08);
  text-align: center;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--brand-purple-700);
  font-size: 0.85rem;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 12px 0 8px;
  font-size: 1.6rem;
  font-weight: var(--font-weight-semibold);
  line-height: 1.25;
`;

const Purpose = styled.p`
  margin: 0 0 8px;
  color: var(--brand-gray-700);
  font-size: 1rem;
  line-height: 1.5;
`;

const Minutes = styled.p`
  margin: 0 0 4px;
  color: var(--brand-gray-600);
  font-size: 0.875rem;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
`;

interface IModuleTitleScreenProps {
  module: TourModule;
  onStart: () => void;
  onExit: () => void;
}

export function ModuleTitleScreen({
  module,
  onStart,
  onExit,
}: IModuleTitleScreenProps) {
  const info = MODULES[module];

  useEffect(() => {
    // The page behind is blanked: put the keyboard on the one thing to do.
    const button = document.querySelector<HTMLButtonElement>(
      '[data-testid="module-title-start"]'
    );
    button?.focus();
  }, [module]);

  return (
    <Backdrop
      data-testid="module-title-screen"
      data-module={module}
      role="dialog"
      aria-modal="true"
      aria-labelledby="module-title-screen-title"
    >
      <Card>
        <Eyebrow>
          <i className="pi pi-verified" aria-hidden />
          Module {module} of 6
        </Eyebrow>
        <Title id="module-title-screen-title">{info.title}</Title>
        <Purpose>{info.purpose}</Purpose>
        {info.minutes !== undefined ? (
          <Minutes data-testid="module-title-minutes">
            About {info.minutes} {info.minutes === 1 ? "minute" : "minutes"}
          </Minutes>
        ) : null}
        <Actions>
          <Button
            type="button"
            data-testid="module-title-start"
            label={`Start Module ${module}`}
            onClick={onStart}
          />
          <Button
            type="button"
            label="Exit"
            className="p-button-text p-button-plain p-button-sm"
            onClick={onExit}
          />
        </Actions>
      </Card>
    </Backdrop>
  );
}
